import { Fragment, useEffect, useState, useMemo } from 'react';
import { usePersonHistory, type PersonHistoryCell, type PersonHistoryDeal } from '../../hooks/usePipeline';
import { DropdownToggle } from '../shared/DropdownToggle';
import { StaffingDealLink, StaffingSourceProvider } from './StaffingDealLink';
import styles from './PersonHistoryModal.module.css';

interface Props {
  email: string;
  orderedEmails?: string[];
  onNavigate?: (email: string) => void;
  onClose: () => void;
}

const ACTIVITY_COLORS = ['#A5D6A7', '#66BB6A', '#43A047', '#2E7D32', '#1B5E20'];

function cellValue(cell: PersonHistoryCell): number {
  return cell.meetings + cell.emails;
}

function cellColor(value: number, max: number): string {
  if (value === 0 || max === 0) return '';
  const ratio = Math.min(value / max, 1);
  const idx = Math.min(Math.floor(ratio * ACTIVITY_COLORS.length), ACTIVITY_COLORS.length - 1);
  return ACTIVITY_COLORS[idx];
}

function formatWeekLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const GROUPS: { key: 'new_deal' | 'portfolio' | 'other'; label: string }[] = [
  { key: 'new_deal', label: 'New Deals' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'other', label: 'Other' },
];

/** Week-by-week sums for a group, used for both the subtotal cells and
 *  the group-local color scale so small groups don't wash out next to big ones. */
function weeklySums(deals: PersonHistoryDeal[], weekCount: number): number[] {
  const sums = new Array(weekCount).fill(0);
  for (const d of deals) {
    for (let i = 0; i < d.cells.length && i < weekCount; i++) {
      sums[i] += cellValue(d.cells[i]);
    }
  }
  return sums;
}

type GroupKey = 'new_deal' | 'portfolio' | 'other';

export function PersonHistoryModal({ email, orderedEmails, onNavigate, onClose }: Props) {
  const [weeks, setWeeks] = useState<12 | 52>(12);
  const [collapsed, setCollapsed] = useState<Set<GroupKey>>(new Set());
  const { data, isLoading, error } = usePersonHistory(email, weeks);

  const toggleGroup = (k: GroupKey) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  // Arrow-key navigation through the ordered list of team members.
  const currentIndex = orderedEmails ? orderedEmails.indexOf(email) : -1;
  const prevEmail = currentIndex > 0 ? (orderedEmails ?? [])[currentIndex - 1] : null;
  const nextEmail =
    currentIndex >= 0 && currentIndex < (orderedEmails ?? []).length - 1
      ? (orderedEmails ?? [])[currentIndex + 1]
      : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (!onNavigate) return;
      if (e.key === 'ArrowRight' && nextEmail) {
        e.preventDefault();
        onNavigate(nextEmail);
      } else if (e.key === 'ArrowLeft' && prevEmail) {
        e.preventDefault();
        onNavigate(prevEmail);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prevEmail, nextEmail, onNavigate, onClose]);

  // Per-deal cell intensity uses a global (cross-group) max so filled cells
  // remain comparable. Subtotal rows use per-group max for their own scale.
  const maxActivity = useMemo(() => {
    if (!data) return 1;
    let max = 0;
    for (const deal of data.deals) {
      for (const cell of deal.cells) {
        const v = cellValue(cell);
        if (v > max) max = v;
      }
    }
    return Math.max(1, max);
  }, [data]);

  const groupedDeals = useMemo(() => {
    if (!data) return new Map<string, PersonHistoryDeal[]>();
    const m = new Map<string, PersonHistoryDeal[]>();
    for (const d of data.deals) {
      const arr = m.get(d.category) || [];
      arr.push(d);
      m.set(d.category, arr);
    }
    // Sort each group by total_activity desc, tiebreak alphabetically
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        const diff = b.total_activity - a.total_activity;
        if (diff !== 0) return diff;
        return a.name.localeCompare(b.name);
      });
    }
    return m;
  }, [data]);

  const totalActivity = data ? data.deals.reduce((s, d) => s + d.total_activity, 0) : 0;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <div className={styles.title}>{data?.person_name || email}</div>
            <div className={styles.subtitle}>
              {data
                ? `${data.deals.length} deals · ${totalActivity} total interactions · ${weeks}-week window`
                : 'Loading…'}
            </div>
          </div>
          <div className={styles.headerControls}>
            {orderedEmails && orderedEmails.length > 1 && (
              <div className={styles.navButtons} title="Left / Right arrows to navigate">
                <button
                  className={styles.navBtn}
                  onClick={() => prevEmail && onNavigate && onNavigate(prevEmail)}
                  disabled={!prevEmail}
                  aria-label="Previous team member"
                >
                  {'\u2039'}
                </button>
                <span className={styles.navPosition}>
                  {currentIndex >= 0 ? `${currentIndex + 1} / ${orderedEmails.length}` : ''}
                </span>
                <button
                  className={styles.navBtn}
                  onClick={() => nextEmail && onNavigate && onNavigate(nextEmail)}
                  disabled={!nextEmail}
                  aria-label="Next team member"
                >
                  {'\u203A'}
                </button>
              </div>
            )}
            <DropdownToggle<12 | 52>
              ariaLabel="Window"
              value={weeks}
              onChange={setWeeks}
              options={[
                { value: 12, label: '12 wk' },
                { value: 52, label: '1 yr' },
              ]}
            />
            <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
              &times;
            </button>
          </div>
        </div>

        <div className={styles.body}>
          {isLoading && <div className={styles.state}>Loading history…</div>}
          {error && <div className={styles.stateError}>Error: {error.message}</div>}
          {data && data.deals.length === 0 && (
            <div className={styles.state}>
              No assignments or activity in the last {weeks} weeks.
            </div>
          )}
          {data && data.deals.length > 0 && (
            // DASH-3 3-3 — the matrix spans both eras in ONE series, and its deal links ask the same
            // component the Staffing tab does where a name should point (the IOT record panel; post-S4
            // there is no second destination).
            <StaffingSourceProvider listId={data.list_id ?? null}>
            <div className={styles.scrollContainer}>
              <table className={`${styles.grid} ${weeks === 12 ? styles.gridWide : ''}`}>
                <thead>
                  <tr>
                    <th className={styles.cornerCell}>Deal</th>
                    {data.weeks.map((w, i) => {
                      // Label every 4th week for 52-week view, every other for 12-week
                      const interval = weeks === 52 ? 4 : 2;
                      const show = i % interval === 0 || i === data.weeks.length - 1;
                      return (
                        <th key={w} className={styles.weekHeader} title={w}>
                          {show ? formatWeekLabel(w) : ''}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {GROUPS.map((g) => {
                    const groupDeals = groupedDeals.get(g.key) || [];
                    if (groupDeals.length === 0) return null;
                    const subtotals = weeklySums(groupDeals, data.weeks.length);
                    const groupMax = Math.max(1, ...subtotals);
                    const colSpan = 1 + data.weeks.length;
                    const isCollapsed = collapsed.has(g.key);
                    return (
                      <Fragment key={g.key}>
                        <tr
                          className={styles.groupRow}
                          onClick={() => toggleGroup(g.key)}
                        >
                          <td colSpan={colSpan} className={styles.groupCell}>
                            <span className={styles.groupChevron}>{isCollapsed ? '\u25B8' : '\u25BE'}</span>
                            {g.label} ({groupDeals.length})
                          </td>
                        </tr>
                        {!isCollapsed && groupDeals.map((deal) => (
                          // DASH-3 3-3 — the key is whichever id this deal's era has; a native deal has
                          // no Affinity id, and `null` as a React key silently collapses rows together.
                          <tr key={deal.entry_id ?? deal.affinity_opportunity_id ?? deal.name}>
                            <td className={styles.dealCell}>
                              <StaffingDealLink
                                deal={{ name: deal.name, entry_id: deal.entry_id ?? null }}
                                className={styles.dealLink}
                              />
                              <span className={styles.dealStage}>{deal.stage}</span>
                            </td>
                            {deal.cells.map((cell) => {
                              const v = cellValue(cell);
                              const label = `${cell.meetings}/${cell.emails}`;
                              const tip = `${cell.week_start}\n${cell.assigned ? `Assigned (${cell.role || 'team'})` : 'Not on team'}\n${cell.meetings} meetings, ${cell.emails} emails`;
                              if (cell.assigned && v > 0) {
                                const bg = cellColor(v, maxActivity);
                                return (
                                  <td
                                    key={cell.week_start}
                                    className={`${styles.cell} ${styles.cellActive}`}
                                    style={{ background: bg }}
                                    title={tip}
                                  >
                                    {label}
                                  </td>
                                );
                              }
                              if (cell.assigned) {
                                return (
                                  <td key={cell.week_start} className={`${styles.cell} ${styles.cellCheck}`} title={tip}>
                                    {'✓'}
                                  </td>
                                );
                              }
                              if (v > 0) {
                                return (
                                  <td key={cell.week_start} className={`${styles.cell} ${styles.cellDriveBy}`} title={tip}>
                                    {label}
                                  </td>
                                );
                              }
                              return <td key={cell.week_start} className={styles.cell} title={tip} />;
                            })}
                          </tr>
                        ))}
                        <tr className={styles.subtotalRow}>
                          <td className={`${styles.dealCell} ${styles.subtotalLabel}`}>
                            {g.label} subtotal
                          </td>
                          {subtotals.map((sum, i) => {
                            const bg = sum > 0 ? cellColor(sum, groupMax) : '';
                            const tip = `${data.weeks[i]}\n${g.label} subtotal: ${sum} interactions`;
                            return (
                              <td
                                key={data.weeks[i]}
                                className={`${styles.cell} ${styles.subtotalCell}`}
                                style={bg ? { background: bg } : undefined}
                                title={tip}
                              >
                                {sum > 0 ? sum : ''}
                              </td>
                            );
                          })}
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </StaffingSourceProvider>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <span className={styles.legendSwatchActivity} />
              Activity (emails + meetings)
            </span>
            <span className={styles.legendItem}>
              <span className={styles.legendSwatchCheck}>{'✓'}</span>
              Assigned, no activity
            </span>
            <span className={styles.legendItem}>
              <span className={styles.legendSwatchDriveBy}>0/3</span>
              Drive-by activity
            </span>
            <span className={styles.legendScale}>
              {ACTIVITY_COLORS.map((c, i) => (
                <span key={i} className={styles.legendSwatch} style={{ background: c }} />
              ))}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
